import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ISecurityGroup, UserMappingItem} from '../../api/types/project.type';
import {ISelectItem} from '../../shared/types';

interface DomainState {
  dropdownId: ISelectItem | null;
  assignedUsers: UserMappingItem[];
  unAssginedUsers: UserMappingItem[];
  billable: boolean;
}

interface ProjectState {
  projectDropdownId: ISelectItem | null;
  role: ISelectItem | null;
  securityGroupDropdownIds: ISelectItem[];
  assignedUsers: UserMappingItem[];
  unAssginedUsers: UserMappingItem[];
  billable: boolean;
  securityGroupList: ISecurityGroup[];
}

interface AssignUsersState {
  domain: DomainState;
  project: ProjectState;
  clearState: boolean;
}

const initialState: AssignUsersState = {
  domain: {
    dropdownId: null,
    assignedUsers: [],
    unAssginedUsers: [],
    billable: false,
  },
  project: {
    projectDropdownId: null,
    role: null,
    securityGroupDropdownIds: [],
    assignedUsers: [],
    unAssginedUsers: [],
    billable: false,
    securityGroupList: [],
  },
  clearState: false,
};

const assignUserSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // DOMAIN
    setTypeDropdownId(
      state,
      action: PayloadAction<{type: string; value: ISelectItem | null}>
    ) {
      if (action.payload.type === 'domain') {
        state.domain.dropdownId = action.payload.value;
      } else {
        state.project.projectDropdownId = action.payload.value;
      }
    },

    // PROJECT ONLY
    setRoleDropdownId(state, action: PayloadAction<ISelectItem | null>) {
      state.project.role = action.payload;
    },
    setSecurityGroupDropdownIds(state, action: PayloadAction<ISelectItem[]>) {
      state.project.securityGroupDropdownIds = action.payload;
    },

    setClearTabData(state, action: PayloadAction<string>) {
      if (action.payload === 'domain') {
        state.domain = {
          dropdownId: null,
          assignedUsers: [],
          unAssginedUsers: [],
          billable: false,
        };
      } else {
        state.project = {
          projectDropdownId: null,
          role: null,
          securityGroupDropdownIds: [],
          assignedUsers: [],
          unAssginedUsers: [],
          billable: false,
          securityGroupList: [],
        };
      }
    },
    setAssignedAndUnassignedUsers(
      state,
      action: PayloadAction<{
        type: string;
        assignedUsers: UserMappingItem[];
        unAssignedUsers: UserMappingItem[];
      }>
    ) {
      const {type, assignedUsers, unAssignedUsers} = action.payload;

      if (type === 'domain') {
        state.domain.assignedUsers = assignedUsers;
        state.domain.unAssginedUsers = unAssignedUsers;
      } else {
        state.project.assignedUsers = assignedUsers;
        state.project.unAssginedUsers = unAssignedUsers;
      }
    },
    setBillableFlag(
      state,
      action: PayloadAction<{type: string; billable: boolean}>
    ) {
      if (action.payload.type === 'domain') {
        state.domain.billable = action.payload.billable;
      } else {
        state.project.billable = action.payload.billable;
      }
    },
    setClearState(state, action: PayloadAction<boolean>) {
      state.clearState = action.payload;
    },
    setSecurityGroupList(state, action: PayloadAction<ISecurityGroup[]>) {
      state.project.securityGroupList = action.payload;
    },
  },
});

export const {
  setTypeDropdownId,
  setRoleDropdownId,
  setSecurityGroupDropdownIds,
  setClearTabData,
  setAssignedAndUnassignedUsers,
  setBillableFlag,
  setClearState,
  setSecurityGroupList,
} = assignUserSlice.actions;

export default assignUserSlice.reducer;
